import type { PayoutView } from "@/services/types";
import { Alert } from "@/components/base/Alert";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

/**
 * Funding panel — the three-line check the organizer performs before
 * authorising: expected collection against what has actually been collected
 * against what is about to be paid out. Where collection is short and the payout
 * is not yet disbursed, the shortfall warning is unmissable.
 */
export function FundingPanel({ payout }: { payout: PayoutView }) {
  const actionable = payout.status === "pending_approval" || payout.status === "approved";
  const hasFunding = payout.expectedCollection > 0;
  const short = actionable && payout.fundingShortfall > 0;
  const pct = payout.expectedCollection > 0 ? Math.round((payout.collected / payout.expectedCollection) * 100) : 0;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="border-b border-background-100 px-4 py-3">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
          <i className="ri-funds-line text-primary-600" />
          Funding
        </h3>
        <p className="mt-0.5 text-xs text-foreground-500">Can we pay this from the month's collection?</p>
      </div>
      <div className="p-4">
        {!hasFunding ? (
          <p className="text-sm text-foreground-500">
            No active collection record for this cycle.
            {payout.status === "disbursed"
              ? " This payout is complete."
              : payout.status === "reversed"
              ? " This payout was reversed."
              : ""}
          </p>
        ) : (
          <>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Expected collection</dt>
                <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={payout.expectedCollection} hideSymbol /></dd>
              </div>
              {payout.commissionAmount && payout.commissionAmount > 0 ? (
                <div className="flex items-center justify-between text-xs text-primary-700">
                  <dt className="text-foreground-600">Organizer commission (included)</dt>
                  <dd className="tnum font-semibold">
                    <CurrencyDisplay amount={payout.commissionAmount} hideSymbol />
                  </dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Collected so far</dt>
                <dd className="tnum font-medium text-primary-700"><CurrencyDisplay amount={payout.collected} hideSymbol /></dd>
              </div>
              <div className="flex items-center justify-between border-t border-background-100 pt-2">
                <dt className="text-foreground-600">Net payable now</dt>
                <dd className="tnum text-base font-bold text-foreground-950"><CurrencyDisplay amount={payout.netPayable} hideSymbol /></dd>
              </div>
            </div>
            <div className="mt-3">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                <div className={`h-full rounded-full ${short ? "bg-warning-500" : "bg-accent-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <p className={`tnum mt-1.5 text-xs ${short ? "font-medium text-warning-700" : "text-foreground-500"}`}>
                {pct}% of the expected collection received
              </p>
            </div>
          </>
        )}

        {short ? (
          <div className="mt-3">
            <Alert variant="warning" title={`Payout exceeds available funds by ${payout.fundingShortfall.toLocaleString("en-IN")}`}>
              Only {payout.collected.toLocaleString("en-IN")} of the expected {payout.expectedCollection.toLocaleString("en-IN")} has
              been collected, yet this payout would pay out {payout.netPayable.toLocaleString("en-IN")}. Disbursing now means covering the
              shortfall yourself — proceed only if you intend to.
            </Alert>
          </div>
        ) : payout.status === "disbursed" ? (
          <p className="mt-3 text-xs text-foreground-500">
            This payout is complete. Any collection gap at the time of disbursement was covered by the organizer to keep the group moving.
          </p>
        ) : payout.status === "reversed" ? (
          <p className="mt-3 text-xs text-amber-700">This payout was reversed. Click "Review & re-approve" to authorise and disburse payment to the customer.</p>
        ) : null}
      </div>
    </div>
  );
}